<script setup lang="ts">
import Customer from "@/customer/interfaces/Customer";
import Beneficiary from "@/customer/interfaces/Beneficiary";
import {computed, nextTick, ref, watch} from "vue";
import {useElementSize} from "@vueuse/core";
import {useRoute, useRouter} from "vue-router";
import {appointmentApi} from "@/container";
import CustomerAppointmentList from "@/appointment/components/consumer-appointment-timeline/CustomerAppointmentList.vue";
import {CalendarHeart, CalendarPlus, Loader2, MessageSquare, Printer} from "@lucide/vue";
import {Button} from "@/core/components/ui/button";
import CustomerCommunicationDialog from "@/customer/components/customer-communication/CustomerCommunicationDialog.vue";
import useCustomerAppointments from "@/appointment/composables/useCustomerAppointments";
import AppointmentShortcutModal from "@/appointment/components/appointment-shortcut/AppointmentShortcutModal.vue";
import {APPOINTMENT_SHORTCUT_QUERY_KEY} from "@/appointment/appointmentShortcut";
import AgendaBookingLinkModal from "@/agenda/components/booking-link/AgendaBookingLinkModal.vue";
import {SelectedPatient} from "@/agenda/components/AgendaPatientSearch.vue";
import useBookingLinkPresets from "@/agenda/composables/useBookingLinkPresets";
import useOrganizationId from "@/core/composables/useOrganizationId";

const props = defineProps<{
    customer: Customer;
    beneficiary?: Beneficiary
    onRefreshCustomer?: () => Promise<void>;
}>();
const {data: appointments} = useCustomerAppointments(props.customer.id);
const loadingExport = ref(false);

/**
 * Below this width the section cannot hold the title and the three actions
 * with their full wording on one row: the actions switch to short labels
 * rather than a smaller text.
 */
const FULL_LABELS_MIN_WIDTH = 860;
const section = ref<HTMLElement>();
const {width: sectionWidth} = useElementSize(section);
const shortLabels = computed(() => sectionWidth.value > 0 && sectionWidth.value < FULL_LABELS_MIN_WIDTH);

/* ------------------------- « Prendre un rendez-vous » ------------------------- */

const route = useRoute();
const router = useRouter();
const organizationId = useOrganizationId();
const {bookingEnabled} = useBookingLinkPresets(() => organizationId.value);

const shortcutOpen = ref(false);
const bookingLinkOpen = ref(false);

/** The patient as the agenda's forms take them, number included for the SMS. */
const selectedPatient = computed<SelectedPatient>(() => ({
    id: props.customer.id,
    name: props.customer.information.name,
    phone: props.customer.information.phone ?? null
}));

/**
 * The detection pop-up opens the hub by sending the main window here with
 * this key; the key is dropped once the hub closes so a reload stays quiet.
 */
watch(() => route.query[APPOINTMENT_SHORTCUT_QUERY_KEY], (flag) => {
    if (flag) {
        shortcutOpen.value = true;
    }
}, {immediate: true});

watch(shortcutOpen, (isOpen) => {
    if (isOpen || !(APPOINTMENT_SHORTCUT_QUERY_KEY in route.query)) {
        return;
    }

    const query = {...route.query};
    delete query[APPOINTMENT_SHORTCUT_QUERY_KEY];

    router.replace({query});
});

/**
 * The hub has closed itself before emitting; the link dialog opens on the
 * next tick so the two never overlap. The calendar is the « Rendez-vous »
 * tab of this very file: the patient stays in context there, and every slot
 * clicked opens a creation already filled with them.
 */
const handleShortcutPick = async (choice: 'link' | 'calendar' | 'journey') => {
    await nextTick();

    if (choice === 'link') {
        bookingLinkOpen.value = true;

        return;
    }

    if (choice === 'journey') {
        await router.push({name: 'agenda.health-journey', query: {customerId: props.customer.id}});

        return;
    }

    await router.push({name: 'customer.appointments', params: {customerId: props.customer.id}});
};

/**
 * Exports the appointments to pdf format.
 */
const exportAppointments = async () => {
    loadingExport.value = true;
    try {
        const blob = await appointmentApi()
            .exportCustomerAppointments(props.customer.id, {active: true});

        const url = window.URL.createObjectURL(blob);

        window.open(url, '_blank');
        window.URL.revokeObjectURL(url);
    } finally {
        loadingExport.value = false;
    }
};
</script>

<template>
  <div ref="section" class="bg-white rounded-lg border overflow-hidden">
    <!--
      One row: the title and its actions. The single primary action is the
      hub, which gathers every way of scheduling — the section itself no
      longer needs a call to action of its own below.
    -->
    <div class="flex flex-wrap items-center justify-between gap-3 px-6 py-3">
      <h2 class="flex items-center gap-2">
        <CalendarHeart class="size-7 text-primary" />
        <!--
          Named after what the section holds, and not after the feature: the
          assisted planning of a series is a screen of its own now, reachable
          from the hub button beside this title.
        -->
        Rendez-vous du patient
      </h2>
      <div class="flex flex-wrap items-center gap-2">
        <CustomerCommunicationDialog
          :customer="customer"
          @certified="() => {
            if(onRefreshCustomer) {
              return onRefreshCustomer();
            }
          }"
        >
          <Button variant="ghost" class="text-primary hover:text-primary" aria-label="Envoyer un SMS">
            <MessageSquare />
            {{ shortLabels ? 'SMS' : 'Envoyer un SMS' }}
          </Button>
        </CustomerCommunicationDialog>
        <Button
          v-if="appointments?.length"
          variant="outline"
          :disabled="loadingExport"
          aria-label="Imprimer la fiche récap"
          @click="exportAppointments"
        >
          <Loader2 v-if="loadingExport" class="animate-spin" />
          <Printer v-else />
          {{ shortLabels ? 'Fiche récap' : 'Imprimer la fiche récap' }}
        </Button>
        <Button class="bg-primary hover:bg-primary/90" aria-label="Prendre un rendez-vous" @click="shortcutOpen = true">
          <CalendarPlus />
          {{ shortLabels ? 'Nouveau RDV' : 'Prendre un rendez-vous' }}
        </Button>
      </div>
    </div>

    <CustomerAppointmentList :customer="customer" class="px-6 pb-4" />
    <AppointmentShortcutModal
      v-model:open="shortcutOpen"
      :customer="customer"
      :booking-enabled="bookingEnabled"
      @pick="handleShortcutPick"
    />
    <AgendaBookingLinkModal
      v-if="bookingEnabled"
      v-model:open="bookingLinkOpen"
      :initial-patient="selectedPatient"
    />
  </div>
</template>

<style scoped>

</style>
